// External Packages
import React, { Suspense, useEffect, useState } from 'react'
import {
  Placeholder,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react'
import { Helmet } from 'react-helmet'
import { after, get } from 'lodash-es'

// Components outside tag-results
import Nav from '../../page-components/Navigation'
import Hero from '../../page-components/Hero'
import Banner from '../../components/Banner'
import Breadcrumb from '../../page-components/Breadcrumb/BreadcrumbList'
import Pagination from '../../components/Pagination'
import CTABanner from '../../components/CTA-Banner'

// Context and utilty outside tag-results
import SearchAppStartContext from '../../quotes/experiences/search-app-start/context'
import SearchAppStart from '../../quotes/experiences/search-app-start/embeds/cta'
import HeroAppStartSwitch from '../../quotes/HeroAppStartSwitch'
import { getSmallBusinessInsuranceBreadcrumb } from '../../util/getSBIBreadcrumb'

// tag-results specific imports
import NoTag from './NoTag'
import TagResults from './TagResults'
import {
  TagResultsAdapterProps,
  TagResultsResponse,
  DataCache,
  RESULTS_PER_PAGE,
} from './types'
import { buildPaginationUrl, getTagAndPageFromPath, updateUrl } from './helpers'
import { fetchGraphQL } from './api'
import styles from './styles.module.scss'
import { addQueryParamsToUrl } from '../../util/imageUrlUtil'
import TagResultsContact from '../../components/Contact-Bar/components/TagResultsContact'
import DynamicJsonLd from './DynamicJsonLd'
import Section from '../../components/Section/Section'

function TagResultsAdapter({ route }: TagResultsAdapterProps) {
  // Component Variables and State setup
  const { tagName, pageNumber: initialPage } = getTagAndPageFromPath(
    route.fields.url
  )
  if (!tagName) {
    return <NoTag />
  }
  const tag: string = tagName
  const breadcrumbs = getSmallBusinessInsuranceBreadcrumb(tagName)
  // Initialize the data cache as an empty dictionary.
  const initialData = route.fields?.tagResultsInitialData
  const [dataCache, setDataCache] = useState<DataCache>(() => {
    if (initialData) {
      return { [initialPage]: initialData }
    }
    return {}
  })
  const [currentPage, setCurrentPage] = useState<number>(initialPage)
  const [isLoading, setIsLoading] = useState<boolean>(!initialData)
  const [hasScrolledOnMount, setHasScrolledOnMount] = useState(false);


  useEffect(() => {
    let isMounted = true

    // this IF handles browser load scrolling 
    // to fixate on top. 
    if (!hasScrolledOnMount && typeof window !== 'undefined') {
      let root: Element | null = null
      if (typeof document !== 'undefined' && document !== null) {
        root = document.querySelector('#root');
      }
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navEntries.length > 0) {
        // The property you want is 'type', not 'entryType'
        const navType = navEntries[0].type;
        // Possible values: "navigate", "reload", "back_forward", "prerender"
        if (['reload', 'back_forward', 'navigate'].includes(navType)) {
          if(root){
            root.scrollIntoView();
          }
        }
      }
      setHasScrolledOnMount(true);
    }

    const currentData = dataCache[currentPage]

    // here we check for SSR data in the injected context. 
    // if it doesn't exist, we client side fetch the data. 
    if (!currentData) {
      console.log('[TagResults] - No SSR data found. Using client side fetch.')
      setIsLoading(true)
      fetchData(currentPage)
        .then((data) => {
          if (isMounted) {
            setDataCache((prev) => ({ ...prev, [currentPage]: data }))
            // Prefetch adjacent pages after fetching current data
            prefetchAdjacentPages(currentPage, data)
          }
        })
        .catch((err) => console.error('Fetch error:', err))
        .finally(() => {
          if (isMounted) {
            setIsLoading(false)
          }
        })
    } else {
      console.log(
        '[TagResults] - SSR data found. Only fetching adjacent pages.'
      )
      // if have data from cache (from SSR), prefetch adjacent pages
      prefetchAdjacentPages(currentPage, currentData)
    }

    return () => {
      isMounted = false
    }
  }, [currentPage, dataCache, hasScrolledOnMount])

  /* START SECTION DATA FETCHING HELPERS */
  async function fetchData(
    pageNumber: number,
    shouldPrefetchAdjacentPages = true
  ): Promise<TagResultsResponse> {
    // If data is already in cache, return it
    if (dataCache[pageNumber]) {
      return dataCache[pageNumber]
    }

    let afterParamNum = 0
    if (pageNumber > 1) {
      afterParamNum = (pageNumber - 1) * RESULTS_PER_PAGE
    }
    const afterParam = afterParamNum.toString()

    try {
      const data = await fetchGraphQL(tag, afterParam)
      if (!data) {
        throw new Error(
          `No data returned for tag ${tag} and page ${pageNumber}`
        )
      }

      // Update cache
      setDataCache((prev) => ({ ...prev, [pageNumber]: data }))

      // Prefetch adjacent pages if allowed
      if (shouldPrefetchAdjacentPages) {
        prefetchAdjacentPages(pageNumber, data)
      }

      return data
    } catch (error) {
      console.error('Fetch error in fetchData:', error)
      throw error
    }
  }

  function prefetchAdjacentPages(pageNumber: number, data: TagResultsResponse) {
    const pageInfo = data.tags.results.pageInfo

    // Prefetch next page if it exists and is not in cache
    if (pageInfo.hasNextPage) {
      const nextPage = pageNumber + 1
      if (!dataCache[nextPage]) {
        fetchData(nextPage, false).catch((err) => console.error(err))
      }
    }

    // Prefetch previous page if it exists and is not in cache
    if (pageInfo.hasPreviousPage && pageNumber > 1) {
      const prevPage = pageNumber - 1
      if (!dataCache[prevPage]) {
        fetchData(prevPage, false).catch((err) => console.error(err))
      }
    }
  }
  /* END SECTION DATA FETCHING HELPERS */

  const currentData = dataCache[currentPage]

  if (isLoading && !currentData) {
    return <div></div>
  }

  if (!currentData) {
    return <div>Sorry! An error occured on this page.</div>
  }

  // Map the items to cards
  const cards = currentData.tags.results.items.map((item) => ({
    title: item.title,
    description: item.description,
    href: item.url,
    image: addQueryParamsToUrl(item.thumbnail, 'mh=220'),
    imageAlt: item.title,
    tag: tagName,
  }))

  // Pagination setup
  const pageInfo = currentData.tags.results.pageInfo
  const totalResults = currentData.tags.results.totalCount

  const nextPageUrl = pageInfo.hasNextPage
    ? buildPaginationUrl(tag, currentPage + 1)
    : ''
  const prevPageUrl = pageInfo.hasPreviousPage
    ? buildPaginationUrl(tag, currentPage - 1)
    : ''
  const nextMeta = nextPageUrl && <link rel="next" href={nextPageUrl} />
  const prevMeta = prevPageUrl && <link rel="prev" href={prevPageUrl} />

  let scrollRoot: Element | null = null
  if (typeof document !== 'undefined' && document !== null) {
    scrollRoot = document.querySelector('.tag-results-article')
  }

  const handleNext = (event: MouseEvent) => {
    event.preventDefault()
    const nextPage = currentPage + 1
    updateUrl(tag, nextPage)
    setCurrentPage(nextPage)
    if (scrollRoot) {
      scrollRoot.scrollIntoView()
    }
  }

  const handlePrevious = (event: MouseEvent) => {
    event.preventDefault()
    if (currentPage > 1) {
      const prevPage = currentPage - 1
      updateUrl(tag, prevPage)
      setCurrentPage(prevPage)
      if (scrollRoot) {
        scrollRoot.scrollIntoView()
      }
    }
  }

  const paging = (
    <Pagination
      testId="TagResults-Pagination"
      currentPage={currentPage}
      totalResults={totalResults}
      pageSize={RESULTS_PER_PAGE}
      onNextClick={handleNext}
      onPreviousClick={handlePrevious}
      nextPath={nextPageUrl}
      prevPath={prevPageUrl}
    />
  )

  const ctaBanner = route?.fields?.bottomCta && (
    <Section>
      <CTABanner
        testId="TagResultsPage-CTABanner"
        {...route.fields.bottomCta.fields}
      >
        <SearchAppStartContext>
          <SearchAppStart
            appStartKey="TagResultsCTABanner"
            rootClass={styles.ctaRoot}
          />
        </SearchAppStartContext>
      </CTABanner>
    </Section>
  )

  const { hero, tpWidgetGlobalSettings } = route.fields
  const { enableTrustPilotHeroWidget } = tpWidgetGlobalSettings.fields
  const heroProps = {
    ...hero.fields,
    applyDefaultImgSizing: true,
    enableTrustPilotHeroWidget,
  }

  const relatedTags = currentData.tags.relatedTags
  const title = `Blogs for small business: ${currentData.tags.tagFriendlyName} | Insureon`
  const desc = `Read blogs for small businesses on the topic of ${currentData.tags.tagFriendlyName.toLowerCase()}. Insureon helps business owners compare insurance quotes from top U.S. carriers.`

  return (
    <>
      <Helmet>
        <title>
          Blogs for small business: {currentData.tags.tagFriendlyName} |
          Insureon
        </title>
        <link rel="canonical" href={route.fields.url} />
        <meta name="robots" content="index, follow" />
        <meta name="description" content={desc} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={route.fields.url} />

        {nextMeta}
        {prevMeta}
      </Helmet>
      <DynamicJsonLd
        canonicalUrl={route.fields.url}
        nextPageUrl={nextPageUrl}
        prevPageUrl={prevPageUrl}
        tagFriendlyName={currentData.tags.tagFriendlyName}
        blogPosts={cards}
      />

      <Nav />
      <Banner />
      <div className="copy tag-results-hero">
        <Hero {...heroProps} tag="div">
          <HeroAppStartSwitch
            quoteButtonTextOverride={heroProps.quoteButtonText?.value}
            darkBg={get(route.fields.hero, 'fields.isDark.value', true)}
          />
        </Hero>
        <Placeholder name="Hero-Placeholder" rendering={route} />
      </div>
      <Suspense>
        <Breadcrumb
          breadcrumbs={breadcrumbs}
          showCurrentPageCrumb={false}
          className={styles.breadcrumb}
        />
        <TagResults
          tagName={currentData.tags.tagFriendlyName}
          cards={cards}
          contact={<TagResultsContact />}
          pagination={paging}
          ctaBanner={ctaBanner}
          tagPath={''}
          isBlog={true}
          relatedTags={relatedTags}
          routeData={route}
        />
      </Suspense>
    </>
  )
}

export default withSitecoreContext()(TagResultsAdapter)
